import {
  type DemoSessionActivity,
  type GetDemoSessionBreakdownArgs,
  useInfiniteScrollFromCache,
  useLazyGetDemoSessionsBreakdownQuery,
} from '@carboncopyco/data';
import { TextCaption } from '@carboncopyco/ui';
import {
  formatPlural,
  getDateFromDaysAgo,
  getNowInFormat,
  getTimezoneName,
} from '@carboncopyco/utils';
import { useEffect, useMemo, useState } from 'react';
import { SessionsBreakdownTable } from '../../../InfoPage/SessionsBreakdownTable';
import { useDashboardProps } from '../../DashboardContext';
import {
  DemosFilter,
  OpportunitiesFilter,
} from '../../SessionBreakdownComponents';
import type { CsvParams } from '../../SessionBreakdownComponents/types';
import { hasProperties } from '../../utils';
import { useTableProps } from './TableContext';
import { VisitsTableCols } from './VisitsTableCols';

export const VisitsBreakdownTable = ({
  setCsvParams,
}: {
  setCsvParams: (options: CsvParams) => void;
}) => {
  const {
    organizationId,
    selectedWorkspaceIds,
    timeRange,
    short,
    internal,
    demoPagePath,
    playbookPagePath,
  } = useDashboardProps();
  const { opportunities, demos, setDemos, setOpportunities } = useTableProps();
  const [showAll, setShowAll] = useState(false);

  const fetchArgs = useMemo<GetDemoSessionBreakdownArgs>(
    () => ({
      organizationId,
      shortSessions: short,
      internalSessions: internal,
      workspaceIds: selectedWorkspaceIds,
      start: getDateFromDaysAgo(timeRange),
      end: getNowInFormat('YYYY-MM-DDTHH:mm:ssZ'),
      timezone: getTimezoneName(),
      opportunities: Object.keys(opportunities),
      demoIds: Object.keys(demos),
      withScreenshots: false,
      resourceTypes: ['shared_presentation'],
    }),
    [
      demos,
      internal,
      opportunities,
      organizationId,
      short,
      timeRange,
      selectedWorkspaceIds,
    ]
  );

  const {
    items,
    isLoading,
    isLoadingBatch,
    error,
    fetchFirstBatch,
    fetchNextBatch,
    totalSize,
  } = useInfiniteScrollFromCache<
    DemoSessionActivity,
    GetDemoSessionBreakdownArgs
  >(useLazyGetDemoSessionsBreakdownQuery, fetchArgs);

  useEffect(() => {
    fetchFirstBatch();
  }, [fetchArgs, fetchFirstBatch]);

  useEffect(() => {
    setCsvParams({
      params: {
        ...fetchArgs,
        resourceTypes: ['shared_presentation'],
      },
      isDisabled: (items && !items.length) || error,
    });
  }, [fetchArgs, items, error, setCsvParams]);

  const clearFilters = () => {
    setOpportunities({});
    setDemos({});
  };

  return (
    <>
      <div className="w-full flex items-center justify-between px-6 py-5">
        <div className="flex gap-3">
          <OpportunitiesFilter />
          <DemosFilter />
        </div>
        {!!items?.length && (
          <TextCaption
            color="text-ds-natural-500"
            label={`${
              totalSize > 7 && !showAll ? 7 : items.length
            } ${formatPlural('session', items.length)} (out of ${totalSize})`}
          />
        )}
      </div>
      <SessionsBreakdownTable<DemoSessionActivity>
        showAll={showAll}
        setShowAll={setShowAll}
        totalSize={totalSize}
        noResults={
          (hasProperties(opportunities) || hasProperties(demos)) &&
          !items?.length
        }
        onClear={clearFilters}
        cols={VisitsTableCols(demoPagePath, playbookPagePath)}
        data={items}
        isLoading={isLoading}
        isLoadingBatch={isLoadingBatch}
        error={error}
        handleFetchNextBatch={fetchNextBatch}
      />
    </>
  );
};
